import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Lightbulb,
  AlertTriangle,
  TrendingDown,
  RefreshCw,
  XCircle,
  ArrowRightLeft,
  Scissors,
  Wallet,
} from "lucide-react";

interface Insight {
  title: string;
  description: string;
  transactions?: string[];
  action: "cancel" | "reduce" | "renegotiate" | "switch" | "budget";
  savingsEstimate?: string | null;
  priority: "high" | "medium" | "low";
}

interface SpendingInsightsProps {
  selectedMonth: string | null;
  selectedSourceIds: string[];
}

const priorityConfig = {
  high: {
    border: "border-l-destructive",
    bg: "bg-destructive/5",
  },
  medium: {
    border: "border-l-warning",
    bg: "bg-warning/5",
  },
  low: {
    border: "border-l-info",
    bg: "bg-info/5",
  },
};

const actionConfig = {
  cancel: {
    icon: XCircle,
    label: "Avsluta",
    class: "bg-destructive/10 text-destructive",
  },
  reduce: {
    icon: TrendingDown,
    label: "Minska",
    class: "bg-warning/10 text-warning",
  },
  renegotiate: {
    icon: ArrowRightLeft,
    label: "Omförhandla",
    class: "bg-info/10 text-info",
  },
  switch: {
    icon: Scissors,
    label: "Byt",
    class: "bg-muted text-foreground",
  },
  budget: {
    icon: Wallet,
    label: "Budgetera",
    class: "bg-success/10 text-success",
  },
};

export function SpendingInsights({ selectedMonth, selectedSourceIds }: SpendingInsightsProps) {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  async function fetchInsights() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedMonth) params.set("month", selectedMonth);
      params.set("sources", selectedSourceIds.join(","));
      const res = await fetch(`/api/insights?${params.toString()}`);
      const data = await res.json();
      setInsights(data);
      setFetched(true);
    } catch {
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setFetched(false);
    setInsights([]);
  }, [selectedMonth, selectedSourceIds]);

  if (!fetched) {
    return (
      <Card>
        <CardContent className="py-8 flex flex-col items-center gap-3">
          <Lightbulb className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Få AI-drivna sparförslag baserat på dina specifika utgifter
          </p>
          <Button onClick={fetchInsights} disabled={loading}>
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Analyserar dina utgifter...
              </>
            ) : (
              <>
                <Lightbulb className="h-4 w-4 mr-2" />
                Analysera utgifter
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (insights.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <p>
            Inga förslag just nu. Ladda upp fler kontoutdrag för bättre analys.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5" />
          Sparförslag
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchInsights}
          disabled={loading}
        >
          <RefreshCw
            className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`}
          />
          Uppdatera
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {insights.map((insight, i) => {
            const pConfig = priorityConfig[insight.priority];
            const aConfig = actionConfig[insight.action] ?? actionConfig.budget;
            const ActionIcon = aConfig.icon;

            return (
              <div
                key={i}
                className={`border-l-4 ${pConfig.border} ${pConfig.bg} rounded-r-lg p-4`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    className={`h-5 w-5 mt-0.5 shrink-0 ${
                      insight.priority === "high"
                        ? "text-destructive"
                        : insight.priority === "medium"
                          ? "text-warning"
                          : "text-info"
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-sm">{insight.title}</h4>
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${aConfig.class}`}
                      >
                        <ActionIcon className="h-3 w-3" />
                        {aConfig.label}
                      </span>
                      {insight.savingsEstimate && (
                        <span className="text-xs font-medium text-success bg-success/15 rounded-full px-2 py-0.5 whitespace-nowrap ml-auto shrink-0">
                          {insight.savingsEstimate}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {insight.description}
                    </p>
                    {insight.transactions && insight.transactions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {insight.transactions.map((tx, j) => (
                          <span
                            key={j}
                            className="text-[11px] bg-background border rounded px-1.5 py-0.5 text-muted-foreground"
                          >
                            {tx}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
